import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Download,
  MessageSquare,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import type { Customer, CustomerPurchase } from '../../types/customer';

interface CustomerDetailsProps {
  customer: Customer;
  purchases: CustomerPurchase[];
  onEdit: () => void;
  onBlock: () => void;
  onDelete: () => void;
  onSendMessage: () => void;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customer,
  purchases,
  onEdit,
  onBlock,
  onDelete,
  onSendMessage
}) => {
  const isBlocked = customer.status === 'blocked';

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.')) {
      onDelete();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {customer.type === 'PF' ? 'CPF' : 'CNPJ'}: {customer.document}
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onSendMessage}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <MessageSquare className="h-5 w-5" />
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Editar
            </button>
            {!isBlocked && (
              <button
                onClick={onBlock}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Bloquear
              </button>
            )}
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isBlocked && (
          <div className="mt-4 p-4 bg-red-50 rounded-md flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Cliente Bloqueado
              </h3>
              <p className="mt-1 text-sm text-red-600">
                Este cliente está bloqueado para novas vendas devido a inadimplência.
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {customer.phone && (
              <div className="flex items-center space-x-2 text-gray-600">
                <Phone className="h-5 w-5" />
                <span>{customer.phone}</span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="h-5 w-5" />
                <span>{customer.email}</span>
              </div>
            )}
            {customer.address_street && (
              <div className="flex items-center space-x-2 text-gray-600">
                <MapPin className="h-5 w-5" />
                <span>
                  {customer.address_street}, {customer.address_number}
                  {customer.address_complement && ` - ${customer.address_complement}`}
                  <br />
                  {customer.address_neighborhood}, {customer.address_city} - {customer.address_state}
                  <br />
                  CEP: {customer.address_zip}
                </span>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {customer.createdAt && (
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar className="h-5 w-5" />
                <span>
                  Cliente desde: {format(new Date(customer.createdAt), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
              </div>
            )}
            {customer.notes && (
              <div className="flex items-start space-x-2 text-gray-600">
                <FileText className="h-5 w-5" />
                <span>{customer.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase History */}
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Histórico de Compras
        </h3>
        
        <div className="space-y-4">
          {purchases.map((purchase) => (
            <div
              key={purchase.id}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-500">
                  {format(purchase.date, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  purchase.status === 'paid' ? 'bg-green-100 text-green-800' :
                  purchase.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {purchase.status === 'paid' ? 'Pago' :
                   purchase.status === 'pending' ? 'Pendente' : 'Atrasado'}
                </span>
              </div>
              
              <div className="space-y-2">
                {purchase.products.map((product) => (
                  <div key={product.id} className="flex justify-between text-sm">
                    <span>{product.name} x{product.quantity}</span>
                    <span className="text-gray-600">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(product.price * product.quantity)}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm font-medium">
                  Total:
                  <span className="ml-2 text-gray-900">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(purchase.total)}
                  </span>
                </div>
                <button className="text-blue-600 hover:text-blue-800 flex items-center space-x-1">
                  <Download className="h-4 w-4" />
                  <span className="text-sm">NF-e</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;